import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import IconPickerField from '../components/IconPickerField';
import ImageUploadField from '../components/ImageUploadField';
import MediaPickerField from '../components/MediaPickerField';
import { FooterQuickLinksField, FooterSocialLinksField } from '../components/FooterDetailFields';
import { ServiceHighlightsField, ServicePackagesField } from '../components/ServiceDetailFields';
import { ProjectCustomFieldsField, ProjectGalleryField } from '../components/ProjectDetailFields';
import { getResourceByKey } from '../config/resources';
import { getModuleConfig, getPageTitle, projectMatchesSlug } from '../config/navigation';
import {
  confirmDelete,
  showCreateSuccess,
  showDeleteSuccess,
  showError,
  showUpdateSuccess,
} from '../utils/adminAlerts';

function makeInitialForm(fields, defaultValues = {}) {
  return fields.reduce((acc, field) => {
    if (defaultValues[field.name] !== undefined) {
      acc[field.name] = defaultValues[field.name];
    } else if (field.type === 'checkbox') {
      acc[field.name] = false;
    } else if (field.type === 'highlights') {
      acc[field.name] = [{ icon: 'LuStar', title: '' }];
    } else if (field.type === 'packages') {
      acc[field.name] = [{ name: '', price: '', features: [''] }];
    } else if (field.type === 'gallery') {
      acc[field.name] = [''];
    } else if (field.type === 'project-fields') {
      acc[field.name] = [{ label: '', value: '', type: 'text' }];
    } else if (field.type === 'footer-social') {
      acc[field.name] = [{ label: '', url: '', iconType: 'icon', icon: 'FaTwitter' }];
    } else if (field.type === 'footer-quick') {
      acc[field.name] = [{ label: '', url: '' }];
    } else {
      acc[field.name] = '';
    }
    return acc;
  }, {});
}

function parseValue(field, value) {
  if (field.type === 'checkbox') return Boolean(value);
  if (field.type === 'number') return value === '' ? null : Number(value);
  if (field.type === 'highlights') {
    return Array.isArray(value)
      ? value
          .filter((item) => item?.title || item?.icon)
          .map((item) => ({
            icon: item.icon || 'LuStar',
            title: item.title || '',
          }))
      : [];
  }
  if (field.type === 'packages') {
    return Array.isArray(value)
      ? value
          .filter((item) => item?.name || item?.price)
          .map((item) => ({
            name: item.name || '',
            price: item.price || '',
            features: Array.isArray(item.features)
              ? item.features.filter((feature) => String(feature || '').trim().length > 0)
              : [],
          }))
      : [];
  }
  if (field.type === 'gallery') {
    return Array.isArray(value) ? value.filter((item) => String(item || '').trim().length > 0) : [];
  }
  if (field.type === 'project-fields') {
    return Array.isArray(value)
      ? value
          .filter((item) => item?.label || item?.value)
          .map((item) => ({
            label: item.label || '',
            value: item.value || '',
            type: item.type || 'text',
          }))
      : [];
  }
  if (field.type === 'footer-social') {
    return Array.isArray(value)
      ? value
          .filter((item) => item?.label || item?.url || item?.icon)
          .map((item) => ({
            label: item.label || '',
            url: item.url || '',
            iconType: item.iconType === 'image' ? 'image' : 'icon',
            icon: item.icon || '',
          }))
      : [];
  }
  if (field.type === 'footer-quick') {
    return Array.isArray(value)
      ? value
          .filter((item) => item?.label || item?.url)
          .map((item) => ({
            label: item.label || '',
            url: item.url || '',
          }))
      : [];
  }
  return value;
}

const AdminCrudPage = () => {
  const { resourceKey } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resource = useMemo(
    () => getModuleConfig(location.pathname, location.search, getResourceByKey),
    [location.pathname, location.search, resourceKey],
  );
  const pageTitle = getPageTitle(location.pathname, location.search);
  const slugFilter = searchParams.get('slug');
  const featuredFilter = searchParams.get('featured') === '1';
  const portfolioFilter = searchParams.get('portfolio') === '1';
  const isPortfolioProject = Boolean(slugFilter && !featuredFilter && !portfolioFilter);
  const access = resource?.access || { create: true, update: true, delete: true };
  const canCreate = access.create !== false;
  const canUpdate = access.update !== false;
  const canDelete = access.delete !== false;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (resource) {
      setFormData(makeInitialForm(resource.fields, resource.defaultValues || {}));
      setEditingId(null);
    }
  }, [resource]);

  const loadData = async () => {
    if (!resource) return;
    try {
      setLoading(true);
      const response = await api.list(resource.endpoint);
      setRows(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [resource, slugFilter, featuredFilter, portfolioFilter]);

  const filteredRows = useMemo(() => {
    let nextRows = rows;

    if (slugFilter) {
      nextRows = nextRows.filter((row) => projectMatchesSlug(row, slugFilter));
    }

    if (featuredFilter) {
      nextRows = nextRows.filter((row) => Boolean(row.isFeatured));
    }

    if (portfolioFilter) {
      nextRows = nextRows.filter((row) => !row.isFeatured);
    }

    return nextRows;
  }, [rows, slugFilter, featuredFilter, portfolioFilter]);

  if (!resource) {
    return <p className="text-red-400">Invalid module.</p>;
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field.name]: field.type === 'checkbox' ? Boolean(value) : value,
    }));
  };

  const resetForm = () => {
    setFormData(makeInitialForm(resource.fields, resource.defaultValues || {}));
    setEditingId(null);
  };

  const handleEdit = (row) => {
    const nextData = makeInitialForm(resource.fields, resource.defaultValues || {});
    resource.fields.forEach((field) => {
      const currentValue = row[field.name];
      if (field.type === 'highlights' || field.type === 'packages') {
        nextData[field.name] = Array.isArray(currentValue) ? currentValue : makeInitialForm([field])[field.name];
      } else if (field.type === 'gallery' || field.type === 'project-fields') {
        nextData[field.name] = Array.isArray(currentValue) && currentValue.length > 0
          ? currentValue
          : makeInitialForm([field])[field.name];
      } else if (field.type === 'footer-social' || field.type === 'footer-quick') {
        nextData[field.name] = Array.isArray(currentValue) && currentValue.length > 0
          ? currentValue
          : makeInitialForm([field])[field.name];
      } else {
        nextData[field.name] = currentValue ?? (field.type === 'checkbox' ? false : '');
      }
    });
    setFormData(nextData);
    setEditingId(row.id);
  };

  useEffect(() => {
    if (!resource.singleRecord || loading) return;

    if (filteredRows.length === 1) {
      const row = filteredRows[0];
      if (editingId !== row.id) {
        handleEdit(row);
      }
    } else if (filteredRows.length === 0 && !editingId) {
      setFormData(makeInitialForm(resource.fields, resource.defaultValues || {}));
    }
  }, [resource, filteredRows, loading, editingId]);

  const handleDelete = async (id) => {
    if (!canDelete) return;
    const confirmed = await confirmDelete(pageTitle);
    if (!confirmed) return;
    try {
      await api.remove(resource.endpoint, id);
      await loadData();
      if (editingId === id) {
        resetForm();
      }
      await showDeleteSuccess(pageTitle);
    } catch (err) {
      const message = err.message || 'Delete failed.';
      setError(message);
      await showError(message, 'Delete failed');
    }
  };

  const handleImageUploadMeta = (meta) => {
    if (resource.key !== 'media-library') return;
    setFormData((prev) => ({
      ...prev,
      fileName: meta.fileName || prev.fileName,
      fileUrl: meta.fileUrl || prev.fileUrl,
      contentType: meta.contentType || prev.contentType,
      size: meta.size ?? prev.size,
    }));
  };

  const renderFieldValue = (field, value) => {
    if (field.type === 'image' && value) {
      return (
        <img
          src={value}
          alt={field.label}
          className="h-10 w-10 rounded-md object-cover border border-slate-200 dark:border-white/10"
        />
      );
    }
    if (field.type === 'color' && value) {
      return (
        <span className="inline-flex items-center gap-2">
          <span
            className="inline-block h-5 w-5 rounded border border-slate-200 dark:border-white/10"
            style={{ backgroundColor: value }}
          />
          <span>{value}</span>
        </span>
      );
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value ?? '');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = resource.fields.reduce((acc, field) => {
        acc[field.name] = parseValue(field, formData[field.name]);
        return acc;
      }, {});

      if (portfolioFilter && !editingId) {
        payload.isFeatured = false;
      }

      if (editingId) {
        if (!canUpdate) {
          const message = 'Update is not allowed for this section.';
          setError(message);
          await showError(message);
          return;
        }
        await api.update(resource.endpoint, editingId, { id: editingId, ...payload });
        await loadData();
        await showUpdateSuccess(pageTitle);
      } else if (canCreate || (isSingleRecord && filteredRows.length === 0)) {
        await api.create(resource.endpoint, payload);
        await loadData();
        if (!resource.singleRecord) {
          resetForm();
        }
        await showCreateSuccess(pageTitle);
      }
    } catch (err) {
      const message = err.message || 'Save failed.';
      setError(message);
      await showError(message, editingId ? 'Update failed' : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const isSingleRecord = Boolean(resource.singleRecord) || isPortfolioProject;
  const isInbox = Boolean(resource.inbox);
  const showForm = isInbox
    ? Boolean(editingId)
    : isSingleRecord
      ? canUpdate || canCreate
      : true;
  const formTitle = isInbox
    ? 'View Message'
    : editingId
      ? 'Edit Content'
      : canCreate
        ? 'Create Content'
        : 'Update Content';
  const submitLabel = isInbox
    ? 'Save Status'
    : editingId || (!canCreate && !(isSingleRecord && filteredRows.length === 0))
      ? 'Update'
      : 'Create';
  const showSubmit =
    editingId
      ? canUpdate
      : canCreate || (isSingleRecord && (canUpdate || filteredRows.length === 0));
  const showCancel = editingId && (isInbox || (canCreate && !isSingleRecord));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="mb-1 text-2xl font-extrabold text-slate-900 dark:text-white">{pageTitle}</h1>
          {resource.sectionHint ? (
            <p className="text-sm text-slate-500 dark:text-gray-400">{resource.sectionHint}</p>
          ) : slugFilter || featuredFilter || portfolioFilter ? (
            <p className="text-sm text-slate-500 dark:text-gray-400">
              {isPortfolioProject
                ? `Editing portfolio detail page at /portfolio/${slugFilter}`
                : 'Showing filtered records for this public website section.'}
            </p>
          ) : null}
        </div>
        {resource.publicPath ? (
          <a
            href={resource.publicPath}
            target="_blank"
            rel="noreferrer"
            className="rounded-xl border border-primary/40 bg-primary/10 px-4 py-2 text-xs font-bold text-primary hover:bg-primary/20"
          >
            View on Website
          </a>
        ) : null}
      </div>

      <div
        className={`grid grid-cols-1 gap-6 ${
          isSingleRecord
            ? 'xl:grid-cols-[1fr_420px]'
            : showForm
              ? 'xl:grid-cols-[380px_1fr]'
              : ''
        }`}
      >
        {showForm ? (
          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#141414] dark:shadow-none">
            <h2 className="mb-3 text-lg font-bold text-slate-900 dark:text-white">{formTitle}</h2>
            {!loading && filteredRows.length === 0 && !canCreate && !editingId && !isSingleRecord ? (
              <p className="mb-3 text-sm text-amber-600 dark:text-amber-400">
                No record found yet. Run the backend seeder or contact support.
              </p>
            ) : null}
            <form onSubmit={handleSubmit} className="space-y-3">
              {resource.fields.map((field) => {
                const isReadOnly = Boolean(field.readOnly);
                return (
                <div key={field.name}>
                  {field.type === 'textarea' ? (
                    <>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                        {field.label}
                      </label>
                      <textarea
                        value={formData[field.name] ?? ''}
                        onChange={(e) => handleInputChange(field, e.target.value)}
                        required={Boolean(field.required) && !isReadOnly}
                        readOnly={isReadOnly}
                        rows={3}
                        className={`w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white ${
                          isReadOnly ? 'cursor-default opacity-90' : ''
                        }`}
                      />
                    </>
                  ) : field.type === 'checkbox' ? (
                    <>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                        {field.label}
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-gray-100">
                        <input
                          type="checkbox"
                          checked={Boolean(formData[field.name])}
                          onChange={(e) => handleInputChange(field, e.target.checked)}
                        />
                        Enabled
                      </label>
                    </>
                  ) : field.type === 'image' ? (
                    <ImageUploadField
                      label={field.label}
                      value={formData[field.name] ?? ''}
                      onChange={(url) => handleInputChange(field, url)}
                      onUploadMeta={handleImageUploadMeta}
                      required={Boolean(field.required)}
                    />
                  ) : field.type === 'media-image' ? (
                    <MediaPickerField
                      label={field.label}
                      value={formData[field.name] ?? ''}
                      onChange={(url) => handleInputChange(field, url)}
                      required={Boolean(field.required)}
                    />
                  ) : field.type === 'icon' ? (
                    <IconPickerField
                      label={field.label}
                      value={formData[field.name] ?? ''}
                      onChange={(value) => handleInputChange(field, value)}
                      required={Boolean(field.required)}
                    />
                  ) : field.type === 'highlights' ? (
                    <ServiceHighlightsField
                      value={formData[field.name]}
                      onChange={(value) => handleInputChange(field, value)}
                    />
                  ) : field.type === 'packages' ? (
                    <ServicePackagesField
                      value={formData[field.name]}
                      onChange={(value) => handleInputChange(field, value)}
                    />
                  ) : field.type === 'gallery' ? (
                    <ProjectGalleryField
                      value={formData[field.name]}
                      onChange={(value) => handleInputChange(field, value)}
                    />
                  ) : field.type === 'project-fields' ? (
                    <ProjectCustomFieldsField
                      value={formData[field.name]}
                      onChange={(value) => handleInputChange(field, value)}
                    />
                  ) : field.type === 'footer-social' ? (
                    <FooterSocialLinksField
                      value={formData[field.name]}
                      onChange={(value) => handleInputChange(field, value)}
                    />
                  ) : field.type === 'footer-quick' ? (
                    <FooterQuickLinksField
                      value={formData[field.name]}
                      onChange={(value) => handleInputChange(field, value)}
                    />
                  ) : field.type === 'color' ? (
                    <>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                        {field.label}
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={formData[field.name] || '#000000'}
                          onChange={(e) => handleInputChange(field, e.target.value)}
                          className="h-10 w-14 cursor-pointer rounded-lg border border-slate-300 bg-white p-1 dark:border-white/15 dark:bg-[#1a1a1a]"
                        />
                        <input
                          type="text"
                          value={formData[field.name] ?? ''}
                          onChange={(e) => handleInputChange(field, e.target.value)}
                          required={Boolean(field.required) && !isReadOnly}
                          readOnly={isReadOnly}
                          placeholder="#000000"
                          className={`w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white ${
                            isReadOnly ? 'cursor-default opacity-90' : ''
                          }`}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                        {field.label}
                      </label>
                      <input
                        type={field.type || 'text'}
                        value={formData[field.name] ?? ''}
                        onChange={(e) => handleInputChange(field, e.target.value)}
                        required={Boolean(field.required) && !isReadOnly}
                        readOnly={isReadOnly}
                        className={`w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white ${
                          isReadOnly ? 'cursor-default opacity-90' : ''
                        }`}
                      />
                    </>
                  )}
                </div>
              );
              })}

              {error ? <p className="text-sm text-red-500 dark:text-red-400">{error}</p> : null}

              <div className="flex gap-2">
                {showSubmit ? (
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-black disabled:opacity-70"
                  >
                    {saving ? 'Saving...' : submitLabel}
                  </button>
                ) : null}
                {showCancel ? (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 dark:border-white/20 dark:text-white"
                  >
                    Cancel
                  </button>
                ) : null}
              </div>
            </form>
          </section>
        ) : null}

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm overflow-x-auto dark:border-white/10 dark:bg-[#141414] dark:shadow-none">
          {isSingleRecord ? (
            <div>
              <h2 className="mb-3 text-lg font-bold text-slate-900 dark:text-white">Live Preview</h2>
              {resource.key === 'hero-sections' ? (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-primary/10 to-white p-4 dark:border-white/10 dark:from-primary/10 dark:to-[#101010]">
                  <div className="mb-3 inline-block rounded-full border border-primary/30 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary dark:bg-[#0f0f0f]">
                    {formData.badgeText || 'Badge Text'}
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    {formData.title || 'Title'}
                    <br />
                    <span className="text-primary">{formData.highlightText || 'Highlight'}</span>
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 dark:text-gray-300">
                    {formData.description || 'Description preview'}
                  </p>
                  <div className="mt-4 inline-flex rounded-full bg-primary px-4 py-2 text-xs font-bold text-black">
                    {formData.primaryButtonText || 'Button'}
                  </div>
                  {formData.imageUrl ? (
                    <img
                      src={formData.imageUrl}
                      alt="Hero preview"
                      className="mt-4 w-full rounded-xl object-cover"
                    />
                  ) : null}
                </div>
              ) : resource.key === 'service-overviews' ? (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-[#101010]">
                  <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    {formData.icon || 'Icon'}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    {formData.title || 'Service Card Title'}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">
                    {formData.shortDescription || 'Card description preview'}
                  </p>
                  <p className="mt-3 text-xs text-primary">
                    Links to /services/{formData.slug || 'slug'}
                  </p>
                </div>
              ) : resource.key === 'services' ? (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-[#101010]">
                  <div className="mb-3 inline-block rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
                    {formData.title || 'Service Title'}
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    Elevate your business with our <span className="text-primary">{formData.title || 'Service'}</span>
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 dark:text-gray-300">
                    {formData.longDescription || formData.shortDescription || 'Service description preview'}
                  </p>
                  {formData.imageUrl ? (
                    <img
                      src={formData.imageUrl}
                      alt="Service preview"
                      className="mt-4 h-40 w-full rounded-xl object-cover"
                    />
                  ) : null}
                  {Array.isArray(formData.highlights) && formData.highlights.length > 0 ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {formData.highlights.map((item, index) => (
                        <span
                          key={index}
                          className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"
                        >
                          {item.title || `Highlight ${index + 1}`}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {Array.isArray(formData.packages) && formData.packages.length > 0 ? (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Packages</p>
                      {formData.packages.map((pkg, index) => (
                        <div
                          key={index}
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm dark:border-white/10"
                        >
                          <p className="font-bold text-slate-900 dark:text-white">{pkg.name || 'Package'}</p>
                          <p className="text-primary">{pkg.price || 'Price'}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : resource.key === 'projects' && isPortfolioProject ? (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-[#101010]">
                  <div className="mb-3 inline-block rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
                    {formData.category || 'Project'}
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    {formData.title || 'Project Title'}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 dark:text-gray-300">
                    {formData.longDescription || formData.description || 'Project description preview'}
                  </p>
                  <p className="mt-3 text-xs text-primary">
                    Public page: /portfolio/{formData.slug || 'slug'}
                  </p>
                  {formData.mainImageUrl ? (
                    <img
                      src={formData.mainImageUrl}
                      alt="Project preview"
                      className="mt-4 h-40 w-full rounded-xl object-cover"
                    />
                  ) : null}
                </div>
              ) : resource.key === 'footer-content' ? (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-primary/20 p-4 dark:border-white/10">
                  {formData.logoUrl ? (
                    <img
                      src={formData.logoUrl}
                      alt="Footer logo preview"
                      className="mb-4 h-10 object-contain"
                    />
                  ) : null}
                  <p className="text-sm text-slate-700 dark:text-gray-200">
                    {formData.description || 'Footer description preview'}
                  </p>
                  {Array.isArray(formData.socialLinks) && formData.socialLinks.length > 0 ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {formData.socialLinks.map((link, index) => (
                        <span
                          key={`${link.label}-${index}`}
                          className="inline-flex items-center gap-2 rounded-full bg-black/10 px-3 py-1 text-xs font-semibold text-black"
                        >
                          {link.iconType === 'image' && link.icon ? (
                            <img src={link.icon} alt={link.label || 'Social'} className="h-4 w-4 object-contain" />
                          ) : (
                            <span>{link.icon || 'Icon'}</span>
                          )}
                          {link.label || 'Social'}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {Array.isArray(formData.quickLinks) && formData.quickLinks.length > 0 ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {formData.quickLinks.map((link, index) => (
                        <span
                          key={`${link.label}-${index}`}
                          className="rounded-full bg-white/70 px-3 py-1 text-xs font-semibold text-slate-700"
                        >
                          {link.label || 'Link'}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <p className="mt-4 text-xs text-slate-600 dark:text-gray-300">
                    &copy; {new Date().getFullYear()} {formData.copyrightText || 'Copyright'}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-slate-500 dark:text-gray-400">Preview updates as you edit the form.</p>
              )}
            </div>
          ) : loading ? (
            <p className="text-slate-600 dark:text-gray-300">Loading...</p>
          ) : (
            <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-white/10">
              <thead>
                <tr className="text-left text-slate-500 dark:text-gray-400">
                  <th className="px-2 py-2">ID</th>
                  {isInbox ? (
                    <>
                      <th className="px-2 py-2">From</th>
                      <th className="px-2 py-2">Subject</th>
                      <th className="px-2 py-2">Status</th>
                    </>
                  ) : (
                    resource.fields.slice(0, 4).map((field) => (
                      <th key={field.name} className="px-2 py-2">
                        {field.label}
                      </th>
                    ))
                  )}
                  {(canUpdate || canDelete) && <th className="px-2 py-2">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-2 py-2 text-slate-700 dark:text-gray-200">{row.id}</td>
                    {isInbox ? (
                      <>
                        <td className="max-w-[220px] px-2 py-2 text-slate-600 dark:text-gray-300">
                          {`${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email}
                        </td>
                        <td className="max-w-[220px] px-2 py-2 text-slate-600 dark:text-gray-300">
                          {row.subject}
                        </td>
                        <td className="px-2 py-2">
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-bold ${
                              row.isRead
                                ? 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-gray-300'
                                : 'bg-primary/15 text-primary'
                            }`}
                          >
                            {row.isRead ? 'Read' : 'New'}
                          </span>
                        </td>
                      </>
                    ) : (
                      resource.fields.slice(0, 4).map((field) => (
                        <td key={field.name} className="max-w-[220px] px-2 py-2 text-slate-600 dark:text-gray-300">
                          {renderFieldValue(field, row[field.name])}
                        </td>
                      ))
                    )}
                    {(canUpdate || canDelete) && (
                      <td className="px-2 py-2">
                        <div className="flex gap-2">
                          {canUpdate ? (
                            <button
                              onClick={() => handleEdit(row)}
                              className="rounded-md border border-primary/50 px-2 py-1 text-xs font-bold text-primary"
                            >
                              {isInbox ? 'Open' : 'Edit'}
                            </button>
                          ) : null}
                          {canDelete ? (
                            <button
                              onClick={() => handleDelete(row.id)}
                              className="rounded-md border border-red-500/60 px-2 py-1 text-xs font-bold text-red-400"
                            >
                              Delete
                            </button>
                          ) : null}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-2 py-6 text-center text-slate-500 dark:text-gray-400">
                      No records found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminCrudPage;
